/**
 * rgt-search.js — Capa RGT sobre el buscador predictivo de Horizon (17-sep-2026).
 *
 * Por qué existe: el buscador de Shopify solo casa prefijos de palabra, así que
 * "GSTS110" no encuentra el GST-S110D-1A y "2100" no encuentra un GA-2100 hasta que
 * el cliente escribe la referencia entera y con guiones. Aquí se carga en diferido
 * un índice del catálogo (assets/rgt-search-index.json, lo publica el sync nocturno
 * con vps/indice_busqueda.py), se casa localmente —referencias sin guion, subcadenas
 * desde 3 caracteres, erratas de una letra— y se le pide al tema que pinte las
 * tarjetas por id (sections/rgt-search-local.liquid), para que precio dual e insignia
 * "Agotado" salgan siempre de Liquid, en vivo.
 *
 * No toca assets/predictive-search.js: Horizon resuelve `on:input="/search"` y
 * `on:keydown="/onSearchKeyDown"` buscando el método en la instancia en cada evento,
 * así que basta con reemplazar esas dos propiedades del elemento. Si el índice no
 * carga (404, JSON inválido, versión distinta) se delega al comportamiento original
 * y el buscador sigue funcionando como antes.
 *
 * También rescata la página /search cuando Shopify devuelve 0 resultados: casa la
 * consulta contra el índice y pinta "Quizás buscabas…" debajo del aviso.
 */

const SEL_COMPONENTE = 'predictive-search-component[data-rgt-index]';
const ESPERA_MS = 120;
const MIN_CARACTERES = 2;
const VERSION_INDICE = 1;

const normalizar = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const compactar = (s) => normalizar(s).replace(/[^a-z0-9]+/g, '');
const palabras = (s) => normalizar(s).split(/[^a-z0-9]+/).filter(Boolean);
const tieneDigito = (s) => /\d/.test(s);
const escapar = (s) =>
  String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const formatear = (n) => n.toLocaleString('es-GT');

/* Palabras que no sirven como sugerencia ("casio de", "reloj para"). */
const RUIDO = new Set([
  'de', 'del', 'la', 'el', 'los', 'las', 'y', 'o', 'en', 'con', 'para', 'por', 'un', 'una',
  'reloj', 'relojes', 'mm', 'x',
]);

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function distancia(a, b, tope) {
  /* Levenshtein con tope: basta saber si está a 1 o 2 letras. */
  if (Math.abs(a.length - b.length) > tope) return tope + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let minFila = i;
    for (let j = 1; j <= b.length; j++) {
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      cur.push(v);
      if (v < minFila) minFila = v;
    }
    if (minFila > tope) return tope + 1;
    prev = cur;
  }
  return prev[b.length];
}

/* ── Índice ──────────────────────────────────────────────────────────────── */

class Indice {
  constructor(json) {
    this.items = json.p.map(([id, titulo, disponible, sku], i) => {
      const w = palabras(titulo);
      const primera = (titulo.split(/\s+/)[0] || '');
      return {
        id,
        t: titulo,
        disp: disponible ? 1 : 0,
        w,
        c: compactar(titulo) + (sku ? compactar(sku) : ''),
        /* La referencia compacta ("gsts110d1a") con la que casa "gsts110", y sus
           tramos entre guiones ("ga", "2100lxb", "1a") para preferir GA-2100 a TW2Y82100. */
        rc: tieneDigito(primera) ? compactar(primera) : '',
        segs: tieneDigito(primera) ? palabras(primera) : [],
        ref: tieneDigito(primera) ? primera : '',
        /* Despertadores y relojes de pared van al final, como en la lista Liquid. */
        pulsera: !/despertador|de pared/.test(normalizar(titulo)),
        i,
      };
    });
    this.marcas = (json.m || []).map(([n, h, c]) => ({ n, h, c, w: palabras(n), k: compactar(n) }));
    this.colecciones = (json.c || []).map(([n, h, c]) => ({ n, h, c, w: palabras(n) }));
    /* Vocabulario para corregir erratas: palabra → frecuencia (las marcas pesan más). */
    this.vocab = new Map();
    for (const it of this.items) {
      for (const p of it.w) if (!tieneDigito(p) && p.length >= 4) this.vocab.set(p, (this.vocab.get(p) || 0) + 1);
    }
    for (const m of this.marcas) {
      for (const p of m.w) if (p.length >= 3) this.vocab.set(p, (this.vocab.get(p) || 0) + 1000);
    }
  }

  /** Puntaje con que un token casa un producto (0 = no casa). */
  static casa(it, tok, tokC, laxo) {
    if (tieneDigito(tok)) {
      if (it.rc) {
        if (it.rc.startsWith(tokC)) return 30;
        if (it.segs.some((sg) => sg.startsWith(tokC))) return 26;
        if (it.rc.includes(tokC)) return 20;
      }
      return it.c.includes(tokC) ? 12 : 0;
    }
    for (let k = 0; k < it.w.length; k++) {
      if (it.w[k].startsWith(tok)) return k === 0 ? 16 : 10;
    }
    if (laxo && tok.length >= 4 && it.c.includes(tok)) return 6;
    return 0;
  }

  _buscar(tokens, laxo) {
    const toksC = tokens.map(compactar);
    const out = [];
    for (const it of this.items) {
      let s = 0;
      let ok = true;
      for (let k = 0; k < tokens.length; k++) {
        const p = Indice.casa(it, tokens[k], toksC[k], laxo);
        if (!p) {
          ok = false;
          break;
        }
        s += p;
      }
      if (ok) out.push({ it, s: s + (it.disp ? 8 : 0) - (it.pulsera ? 0 : 15) });
    }
    return out;
  }

  /** Erratas de una letra (dos si la palabra es larga) contra el vocabulario. */
  _corregir(tokens) {
    let cambio = false;
    const alt = tokens.map((tok) => {
      if (tieneDigito(tok) || tok.length < 4) return tok;
      for (const p of this.vocab.keys()) if (p.startsWith(tok)) return tok;
      const tope = tok.length >= 7 ? 2 : 1;
      let mejor = null;
      let mejorFrec = 0;
      for (const [p, frec] of this.vocab) {
        if (distancia(tok, p, tope) <= tope && frec > mejorFrec) {
          mejor = p;
          mejorFrec = frec;
        }
      }
      if (mejor) cambio = true;
      return mejor || tok;
    });
    return cambio ? alt : null;
  }

  _marcas(tokens) {
    const alfa = tokens.filter((t) => !tieneDigito(t) && t.length >= 2);
    if (!alfa.length) return [];
    return this.marcas
      .filter((m) => alfa.some((t) => m.w.some((p) => p.startsWith(t)) || m.k.startsWith(t)))
      .sort((a, b) => b.c - a.c)
      .slice(0, 4);
  }

  _colecciones(tokens) {
    const alfa = tokens.filter((t) => !tieneDigito(t) && t.length >= 2 && !RUIDO.has(t));
    if (!alfa.length) return [];
    return this.colecciones
      .filter((c) => alfa.every((t) => c.w.some((p) => p.startsWith(t))))
      .sort((a, b) => b.c - a.c)
      .slice(0, 4);
  }

  _sugerencias(tokens, encontrados) {
    const ultimo = tokens[tokens.length - 1];
    if (!ultimo || tieneDigito(ultimo)) return [];
    const cuenta = new Map();
    for (const { it } of encontrados.slice(0, 400)) {
      for (const p of it.w) {
        if (p === ultimo || p.length < 3 || tieneDigito(p) || RUIDO.has(p) || !p.startsWith(ultimo)) continue;
        cuenta.set(p, (cuenta.get(p) || 0) + 1);
      }
    }
    const base = tokens.slice(0, -1);
    return [...cuenta.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([p]) => [...base, p].join(' '));
  }

  match(consulta, { limite = 8 } = {}) {
    const tokens = palabras(consulta);
    const res = {
      consulta,
      tokens,
      total: 0,
      items: [],
      marcas: [],
      colecciones: [],
      sugerencias: [],
      corregido: null,
    };
    if (!tokens.length) return res;
    let hallados = this._buscar(tokens, false);
    if (!hallados.length) hallados = this._buscar(tokens, true);
    if (!hallados.length) {
      const alt = this._corregir(tokens);
      if (alt) {
        hallados = this._buscar(alt, false);
        if (hallados.length) {
          res.corregido = alt.join(' ');
          res.tokens = alt;
        }
      }
    }
    hallados.sort((a, b) => b.s - a.s || a.it.i - b.it.i);
    res.total = hallados.length;
    res.items = hallados.slice(0, limite).map((h) => h.it);
    res.marcas = this._marcas(res.tokens);
    res.colecciones = this._colecciones(res.tokens);
    res.sugerencias = this._sugerencias(res.tokens, hallados);
    return res;
  }

  /**
   * La consulta que conviene mandarle a /search. Para una referencia a medias
   * ("gsts110") devuelve el prefijo común de las referencias casadas ("GST-S110"),
   * que el buscador de Shopify sí entiende. Para palabras, la versión corregida.
   */
  reescribir(consulta) {
    const r = this.match(consulta, { limite: 24 });
    if (!r.total) return consulta;
    if (!tieneDigito(consulta)) return r.corregido || consulta;
    const refs = r.items.map((it) => it.ref).filter(Boolean);
    if (!refs.length) return consulta;
    let pref = refs[0];
    for (const ref of refs) {
      let i = 0;
      while (i < pref.length && i < ref.length && pref[i].toLowerCase() === ref[i].toLowerCase()) i++;
      pref = pref.slice(0, i);
      if (!pref) break;
    }
    pref = pref.replace(/[-.\s]+$/, '');
    if (pref.length >= 3 && compactar(pref).includes(compactar(consulta))) return pref;
    return consulta;
  }
}

/* ── Resaltado ───────────────────────────────────────────────────────────── */

/** Envuelve en <mark> lo que casa con los tokens, respetando tildes y guiones del original. */
function resaltar(texto, tokens) {
  if (!texto || !tokens.length) return escapar(texto || '');
  const chars = [...texto];
  const marcado = new Array(chars.length).fill(false);
  /* Mapa palabra a palabra (normalizada, misma longitud que el original). */
  const normal = chars.map((ch) => normalizar(ch)[0] || ch);
  /* Mapa compacto → posición original, para las referencias sin guion. */
  const compIdx = [];
  let comp = '';
  normal.forEach((ch, i) => {
    if (/[a-z0-9]/.test(ch)) {
      comp += ch;
      compIdx.push(i);
    }
  });
  for (const tok of tokens) {
    if (tieneDigito(tok)) {
      const tc = compactar(tok);
      let from = 0;
      let at;
      while (tc && (at = comp.indexOf(tc, from)) !== -1) {
        for (let k = at; k < at + tc.length; k++) marcado[compIdx[k]] = true;
        from = at + tc.length;
      }
    } else {
      const n = normal.join('');
      let from = 0;
      let at;
      while ((at = n.indexOf(tok, from)) !== -1) {
        const inicioPalabra = at === 0 || !/[a-z0-9]/.test(normal[at - 1]);
        if (inicioPalabra) for (let k = at; k < at + tok.length; k++) marcado[k] = true;
        from = at + tok.length;
      }
    }
  }
  let html = '';
  let abierto = false;
  chars.forEach((ch, i) => {
    if (marcado[i] && !abierto) {
      html += '<mark>';
      abierto = true;
    } else if (!marcado[i] && abierto) {
      html += '</mark>';
      abierto = false;
    }
    html += escapar(ch);
  });
  if (abierto) html += '</mark>';
  return html;
}

function resaltarTarjetas(raiz, tokens) {
  raiz.querySelectorAll('[data-rgt-title]').forEach((el) => {
    el.innerHTML = resaltar(el.textContent.trim(), tokens);
  });
}

/* ── Carga del índice (compartida entre el modal y la página de resultados) ── */

const cargas = new Map();
function cargarIndice(url) {
  if (!cargas.has(url)) {
    cargas.set(
      url,
      fetch(url, { credentials: 'omit' })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
        .then((j) => {
          if (!j || j.v !== VERSION_INDICE || !Array.isArray(j.p) || !j.p.length) throw new Error('índice inválido');
          return new Indice(j);
        })
        .catch((e) => {
          console.warn('rgt-search: sin índice local, se usa la búsqueda de Shopify.', e);
          return null;
        })
    );
  }
  return cargas.get(url);
}

/** Pide al tema las tarjetas de esos ids, ya en el orden pedido. */
async function tarjetasPorIds(searchUrl, seccion, ids, signal) {
  const q = ids.map((id) => `id:${id}`).join(' OR ');
  const url = `${searchUrl}?q=${encodeURIComponent(q)}&type=product&section_id=${encodeURIComponent(seccion)}`;
  const r = await fetch(url, { signal });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  const doc = new DOMParser().parseFromString(await r.text(), 'text/html');
  const grid = doc.querySelector('.rgt-search-grid');
  return grid ? [...grid.children] : [];
}

function limiteTarjetas() {
  if (window.matchMedia('(min-width: 1400px)').matches) return 8;
  if (window.matchMedia('(min-width: 750px)').matches) return 6;
  return 8;
}

/* ── Marcado del panel ───────────────────────────────────────────────────── */

function filaRail(href, html, cuenta) {
  return `<li><a class="rgt-sr__row" href="${escapar(href)}">${html}<small>${formatear(cuenta)}</small></a></li>`;
}

function htmlRail(res, cfg) {
  const partes = [];
  if (res.marcas.length) {
    partes.push(
      `<section class="rgt-sr__sec"><h4 class="rgt-search-section-title">Marcas</h4><ul class="rgt-sr__list">` +
        res.marcas
          .map((m) =>
            filaRail(
              m.h ? `/collections/${m.h}` : `/collections/vendors?q=${encodeURIComponent(m.n)}`,
              `<span>${resaltar(m.n, res.tokens)}</span>`,
              m.c
            )
          )
          .join('') +
        `</ul></section>`
    );
  }
  if (res.colecciones.length) {
    partes.push(
      `<section class="rgt-sr__sec"><h4 class="rgt-search-section-title">Colecciones</h4><ul class="rgt-sr__list">` +
        res.colecciones.map((c) => filaRail(`/collections/${c.h}`, `<span>${resaltar(c.n, res.tokens)}</span>`, c.c)).join('') +
        `</ul></section>`
    );
  }
  if (res.sugerencias.length) {
    partes.push(
      `<section class="rgt-sr__sec"><h4 class="rgt-search-section-title">Sugerencias</h4><ul class="rgt-sr__list">` +
        res.sugerencias
          .map(
            (s) =>
              `<li><button type="button" class="rgt-sr__row" data-rgt-sug="${escapar(s)}"><span>${resaltar(s, res.tokens)}</span></button></li>`
          )
          .join('') +
        `</ul></section>`
    );
  }
  if (!partes.length) return '';
  return `<aside class="rgt-sr__rail">${partes.join('')}</aside>`;
}

function htmlSinResultados(res, cfg, topHtml) {
  const marcas = cfg.indice.marcas.slice(0, 6);
  const texto = encodeURIComponent(`Hola, busco el modelo "${res.consulta}" y no lo encontré en la web.`);
  return (
    `<div class="rgt-sr__none" data-rgt-none>` +
    `<span class="predictive-search-results__no-results" hidden></span>` +
    `<p class="rgt-sr__none-title">No encontramos «${escapar(res.consulta)}»</p>` +
    `<ul class="rgt-sr__tips">` +
    `<li>Revisa la referencia: basta con una parte, por ejemplo <b>2100</b> o <b>GST-S110</b>.</li>` +
    `<li>Prueba solo con la marca o con menos palabras.</li>` +
    `</ul>` +
    (marcas.length
      ? `<div class="rgt-sr__chips">${marcas
          .map(
            (m) =>
              `<a class="rgt-sr__chip" href="${escapar(m.h ? `/collections/${m.h}` : `/collections/vendors?q=${encodeURIComponent(m.n)}`)}">${escapar(m.n)}</a>`
          )
          .join('')}</div>`
      : '') +
    (cfg.wa
      ? `<a class="rgt-sr__wa" href="${escapar(cfg.wa)}?text=${texto}" target="_blank" rel="noopener">` +
        `<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.6.8-.8 1c-.1.2-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3.1-.2 0-.3 0-.4l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.3.8 3.1.6a2.7 2.7 0 0 0 1.8-1.2 2.2 2.2 0 0 0 .1-1.2c0-.1-.2-.2-.4-.3Z"/></svg>` +
        `<span>¿Tienes la referencia? Escríbenos y te lo conseguimos</span></a>`
      : '') +
    (topHtml ? `<div class="rgt-sr__top">${topHtml}</div>` : '') +
    `</div>`
  );
}

function htmlResultados(res, cfg, n) {
  const rail = htmlRail(res, cfg);
  const skeleton = Array.from({ length: n }, () => `<div class="rgt-sr__sk" aria-hidden="true"></div>`).join('');
  const verTodos = `${cfg.searchUrl}?q=${encodeURIComponent(cfg.indice.reescribir(res.consulta))}&type=product`;
  const nota = res.corregido
    ? `<p class="rgt-sr__nota">Mostrando resultados para <b>«${escapar(res.corregido)}»</b></p>`
    : '';
  return (
    `<div class="rgt-sr${rail ? '' : ' rgt-sr--solo'}" data-search-results data-rgt-results>` +
    rail +
    `<div class="rgt-sr__main">` +
    nota +
    `<div class="rgt-search-section-head">` +
    `<h4 class="rgt-search-section-title">Productos<span class="rgt-search-count"> · ${formatear(res.total)}</span></h4>` +
    (res.total > n ? `<a class="rgt-search-ver-todos" href="${escapar(verTodos)}">Ver todos →</a>` : '') +
    `</div>` +
    `<div class="rgt-search-grid" role="listbox" id="rgt-search-listbox" aria-label="Productos">${skeleton}</div>` +
    (res.total === 1 && res.items[0] ? `<div data-single-result-url="/products/"></div>` : '') +
    `</div></div>`
  );
}

/* ── El modal ────────────────────────────────────────────────────────────── */

function montar(el) {
  if (el.__rgt) return;
  el.__rgt = true;

  const cfg = {
    indice: null,
    urlIndice: el.dataset.rgtIndex,
    seccion: el.dataset.rgtLocalSection || 'rgt-search-local',
    searchUrl: el.dataset.rgtSearchUrl || (window.Theme && Theme.routes && Theme.routes.search_url) || '/search',
    wa: el.dataset.rgtWa || '',
  };
  const original = { search: el.search, onSearchKeyDown: el.onSearchKeyDown, resetSearch: el.resetSearch };
  const estado = { termino: '', activo: -1, ctrl: null, cache: new Map(), top: '', vacio: '', urlUnico: '' };
  const input = () => el.refs.searchInput;
  const panel = () => el.refs.predictiveSearchResults;
  const coarse = window.matchMedia('(pointer: coarse)').matches;

  const guardarTop = () => {
    const vacioEl = panel().querySelector('[data-rgt-empty]');
    if (vacioEl) {
      /* Un reloj recién visto no se repite en «Lo más buscado». */
      const vistos = new Set([...vacioEl.querySelectorAll('.rgt-search-viewed [data-rgt-id]')].map((c) => c.dataset.rgtId));
      if (vistos.size) {
        vacioEl.querySelectorAll('[data-rgt-top] [data-rgt-id]').forEach((c) => {
          if (vistos.has(c.dataset.rgtId)) c.remove();
        });
      }
    }
    const t = el.querySelector('[data-rgt-top]');
    if (t) estado.top = t.outerHTML;
    if (!estado.termino && vacioEl) estado.vacio = panel().innerHTML;
  };

  /** Vuelve al estado vacío en seco, sin esperar a la red. */
  const vaciar = () => {
    if (estado.ctrl) estado.ctrl.abort();
    estado.ctrl = null;
    estado.activo = -1;
    estado.urlUnico = '';
    el.removeAttribute('aria-busy');
    const inp = input();
    inp.setAttribute('aria-expanded', 'false');
    inp.removeAttribute('aria-activedescendant');
    if (estado.vacio) {
      panel().innerHTML = estado.vacio;
      panel().scrollTo(0, 0);
      return true;
    }
    return false;
  };

  const preparar = () => {
    guardarTop();
    return cargarIndice(cfg.urlIndice).then((idx) => {
      cfg.indice = idx;
      return idx;
    });
  };

  const seleccionar = (i) => {
    const items = [...el.querySelectorAll('[data-rgt-results] [data-rgt-item]')];
    items.forEach((it) => {
      it.setAttribute('aria-selected', 'false');
      it.classList.remove('is-active');
    });
    estado.activo = items.length ? Math.max(-1, Math.min(i, items.length - 1)) : -1;
    const inp = input();
    if (estado.activo < 0) {
      inp.removeAttribute('aria-activedescendant');
      return;
    }
    const it = items[estado.activo];
    it.setAttribute('aria-selected', 'true');
    it.classList.add('is-active');
    if (!it.id) it.id = `rgt-opt-${estado.activo}`;
    inp.setAttribute('aria-activedescendant', it.id);
    it.scrollIntoView({ block: 'nearest' });
  };

  const escribir = (texto) => {
    const inp = input();
    inp.value = texto;
    inp.focus();
    inp.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: texto }));
  };

  const destinoBusqueda = (termino) =>
    `${cfg.searchUrl}?q=${encodeURIComponent(cfg.indice ? cfg.indice.reescribir(termino) : termino)}&type=product`;

  const pintar = async (termino) => {
    const idx = cfg.indice;
    const n = limiteTarjetas();
    const res = idx.match(termino, { limite: n });
    guardarTop();
    if (estado.ctrl) estado.ctrl.abort();
    estado.ctrl = null;
    estado.activo = -1;
    estado.urlUnico = '';
    const cont = panel();
    input().setAttribute('aria-expanded', 'true');

    if (!res.total) {
      cont.innerHTML = htmlSinResultados(res, cfg, estado.top);
      el.removeAttribute('aria-busy');
      cont.scrollTo(0, 0);
      return;
    }

    cont.innerHTML = htmlResultados(res, cfg, Math.min(n, res.total));
    cont.scrollTo(0, 0);
    const grid = cont.querySelector('#rgt-search-listbox');
    const ids = res.items.map((it) => it.id);
    const clave = ids.join(',');
    let tarjetas = estado.cache.get(clave);
    if (!tarjetas) {
      const ctrl = new AbortController();
      estado.ctrl = ctrl;
      el.setAttribute('aria-busy', 'true');
      try {
        tarjetas = await tarjetasPorIds(cfg.searchUrl, cfg.seccion, ids, ctrl.signal);
        if (estado.cache.size > 30) estado.cache.delete(estado.cache.keys().next().value);
        estado.cache.set(clave, tarjetas);
      } catch (e) {
        if (ctrl.signal.aborted) return;
        grid.innerHTML = `<p class="rgt-sr__error">No pudimos cargar los productos. Intenta de nuevo.</p>`;
        el.removeAttribute('aria-busy');
        return;
      }
      if (ctrl.signal.aborted) return;
      el.removeAttribute('aria-busy');
    }
    if (!grid.isConnected || estado.termino !== termino) return;
    grid.replaceChildren(...tarjetas.map((t) => t.cloneNode(true)));
    resaltarTarjetas(grid, res.tokens);
    grid.querySelectorAll('[data-rgt-item]').forEach((it, i) => {
      it.id = `rgt-opt-${i}`;
    });
    if (res.total === 1) {
      const a = grid.querySelector('a[href]');
      const marcador = cont.querySelector('[data-single-result-url]');
      if (a && marcador) {
        estado.urlUnico = a.getAttribute('href');
        marcador.dataset.singleResultUrl = estado.urlUnico;
      }
    }
    const boton = el.querySelector('.predictive-search__search-button [data-rgt-ver-todos-texto]');
    if (boton) boton.textContent = res.total > 1 ? `Ver los ${formatear(res.total)} resultados` : 'Ver todos los resultados';
  };

  const buscar = debounce(async () => {
    const termino = input().value.trim();
    if (termino !== estado.termino) return;
    if (!termino) return;
    if (!cfg.indice) await preparar();
    if (!cfg.indice) {
      original.search.call(el, { inputType: 'insertText' });
      return;
    }
    if (termino.length < MIN_CARACTERES) return;
    if (termino !== input().value.trim()) return;
    pintar(termino);
  }, ESPERA_MS);

  /* on:input="/search" — Horizon la busca en la instancia en cada evento. */
  el.search = (event) => {
    const termino = input().value.trim();
    if (termino === estado.termino) return;
    estado.termino = termino;
    el.refs.resetButton.hidden = !termino;
    if (!termino) {
      /* Sin copia del estado vacío (primer uso), lo pinta el reset original de Horizon. */
      if (!vaciar()) original.search.call(el, event && event.inputType ? event : { inputType: 'deleteContentBackward' });
      return;
    }
    buscar();
  };

  /* on:click="/resetSearch" (botón Borrar): en seco y con el foco en el campo. */
  el.resetSearch = () => {
    const inp = input();
    inp.value = '';
    estado.termino = '';
    el.refs.resetButton.hidden = true;
    if (!vaciar()) original.resetSearch.call(el, true);
    inp.focus();
  };

  /* on:keydown="/onSearchKeyDown" — solo cuando el panel es nuestro. */
  el.onSearchKeyDown = (event) => {
    const propio = el.querySelector('[data-rgt-results], [data-rgt-none]');
    if (!propio || event.key === 'Escape') {
      if (event.key === 'Escape') estado.termino = '';
      return original.onSearchKeyDown.call(el, event);
    }
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        seleccionar(estado.activo + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        seleccionar(estado.activo - 1);
        break;
      case 'Enter': {
        event.preventDefault();
        const items = [...el.querySelectorAll('[data-rgt-results] [data-rgt-item]')];
        const activo = estado.activo >= 0 ? items[estado.activo] : null;
        const enlace = activo && activo.querySelector('a[href]');
        if (enlace) {
          enlace.click();
        } else if (estado.urlUnico) {
          window.location.href = estado.urlUnico;
        } else {
          const termino = input().value.trim();
          if (termino) window.location.href = destinoBusqueda(termino);
        }
        break;
      }
      default:
        break;
    }
  };

  /* Sugerencias del rail: rellenan el campo y vuelven a buscar. */
  el.addEventListener('click', (event) => {
    const sug = event.target.closest('[data-rgt-sug]');
    if (sug) {
      event.preventDefault();
      escribir(sug.dataset.rgtSug);
      return;
    }
    /* En táctil, Horizon re-enfoca el campo con cada toque en blanco y sube el teclado. */
    if (coarse && !event.target.closest('a, button, input, [data-rgt-item]')) input().blur();
  });

  /* "Ver todos los resultados" y Enter nativo: una sola navegación, con la consulta reescrita. */
  el.refs.form?.addEventListener('submit', (event) => {
    const termino = input().value.trim();
    if (!termino || !cfg.indice) return;
    event.preventDefault();
    window.location.href = destinoBusqueda(termino);
  });

  const dialogo = el.closest('dialog-component');
  if (dialogo) {
    dialogo.addEventListener('dialog:open', () => {
      preparar();
    });
    dialogo.addEventListener('dialog:close', () => {
      estado.termino = '';
      estado.activo = -1;
      estado.urlUnico = '';
      if (estado.ctrl) estado.ctrl.abort();
      el.removeAttribute('aria-busy');
      input().setAttribute('aria-expanded', 'false');
      input().removeAttribute('aria-activedescendant');
    });
  }
  input().addEventListener('focus', () => preparar(), { once: true });

  /* Si el reset asíncrono de Horizon (al abrir con «vistos», al cerrar, con Escape) aterriza
     después de que pintamos resultados, los pisa: se detecta y se vuelve a pintar (las
     tarjetas ya están en caché). Y cuando lo que aterriza es el estado vacío, se guarda. */
  let repintando = false;
  new MutationObserver(() => {
    const raiz = panel();
    if (!estado.termino) {
      if (raiz.querySelector('[data-rgt-empty]')) guardarTop();
      return;
    }
    if (estado.termino.length < MIN_CARACTERES || !cfg.indice || repintando) return;
    if (raiz.querySelector('[data-rgt-results], [data-rgt-none]')) return;
    repintando = true;
    requestAnimationFrame(() => {
      repintando = false;
      if (estado.termino && !raiz.querySelector('[data-rgt-results], [data-rgt-none]')) pintar(estado.termino);
    });
  }).observe(panel(), { childList: true });
  if ('requestIdleCallback' in window) requestIdleCallback(() => guardarTop());
}

/* ── Página de resultados: rescate cuando Shopify no encontró nada ───────── */

async function rescatarPaginaResultados() {
  const aviso = document.querySelector('.search-results__no-results');
  if (!aviso) return;
  const q = (new URLSearchParams(window.location.search).get('q') || '').trim();
  if (!q || q.startsWith('id:')) return;
  const comp = document.querySelector(SEL_COMPONENTE);
  if (!comp) return;
  const idx = await cargarIndice(comp.dataset.rgtIndex);
  if (!idx) return;
  const res = idx.match(q, { limite: 12 });
  if (!res.total) return;
  const searchUrl = comp.dataset.rgtSearchUrl || '/search';
  const reescrita = idx.reescribir(q);
  const bloque = document.createElement('div');
  bloque.className = 'rgt-sr-rescate';
  bloque.innerHTML =
    `<div class="rgt-search-section-head">` +
    `<h2 class="rgt-sr-rescate__titulo">Quizás buscabas${res.corregido ? ` «${escapar(res.corregido)}»` : ''}<span class="rgt-search-count"> · ${formatear(res.total)} ${res.total === 1 ? 'producto' : 'productos'}</span></h2>` +
    (res.total > 12 && reescrita !== q
      ? `<a class="rgt-search-ver-todos" href="${escapar(`${searchUrl}?q=${encodeURIComponent(reescrita)}&type=product`)}">Ver todos →</a>`
      : '') +
    `</div><div class="rgt-search-grid"></div>`;
  aviso.insertAdjacentElement('afterend', bloque);
  try {
    const tarjetas = await tarjetasPorIds(searchUrl, comp.dataset.rgtLocalSection || 'rgt-search-local', res.items.map((it) => it.id));
    const grid = bloque.querySelector('.rgt-search-grid');
    tarjetas.forEach((t) => t.removeAttribute('role'));
    grid.replaceChildren(...tarjetas);
    resaltarTarjetas(grid, res.tokens);
  } catch (e) {
    bloque.remove();
  }
}

/* ── Arranque ────────────────────────────────────────────────────────────── */

function arrancar() {
  customElements.whenDefined('predictive-search-component').then(() => {
    document.querySelectorAll(SEL_COMPONENTE).forEach(montar);
  });
  rescatarPaginaResultados();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', arrancar);
} else {
  arrancar();
}
